/**
 * Generic spreadsheet → dataset parser.
 *
 * The first stage of the data-import pipeline: turn an uploaded `.xlsx` / `.xls` / `.csv` file into a
 * plain, domain-agnostic `ParsedDataset` (typed columns + row objects + the raw matrix). Nothing here
 * knows about Cisco inventories or any other domain — presets (see `presets/`) interpret the dataset;
 * this layer only reads bytes into rows.
 *
 * SheetJS (`xlsx`, already a dependency) is loaded with a dynamic `import()` so the ~1MB library stays
 * out of the main bundle and is only fetched when someone actually imports a file — matching how
 * `SafeReportStep` and `ResourceFileViewer` already use it. SheetJS auto-detects CSV from the raw
 * bytes, so the same `read(arrayBuffer, { type: 'array' })` path handles all three extensions; this is
 * what closes the CSV gap the standalone example app never had (it was `.xlsx`-only).
 */

export type DatasetSourceKind = 'xlsx' | 'csv';

export interface DatasetSourceMeta {
  fileName: string;
  /** ISO timestamp of when the file was parsed in the browser. */
  parsedAt: string;
  kind: DatasetSourceKind;
  /** The sheet the rows were read from (first non-empty sheet). */
  sheetName?: string;
}

export interface ParsedDataset {
  /** Column names, in order. Blank/duplicate header cells are backfilled to `Column N` / `name_2`. */
  columns: string[];
  /** One object per data row, keyed by `columns`. Cell values are strings/numbers/booleans as SheetJS typed them. */
  rows: Record<string, any>[];
  rowCount: number;
  sourceMeta: DatasetSourceMeta;
  /**
   * The raw header+data matrix (`any[][]`) exactly as SheetJS emitted it. Presets that need a
   * positional scan — e.g. detecting a pivot-table layout where categories are inline rows rather
   * than a column — read this instead of `rows`.
   */
  matrix: any[][];
}

const CSV_EXTENSIONS = ['csv'];
const EXCEL_EXTENSIONS = ['xlsx', 'xls', 'xlsm'];

/** Longest run of leading non-empty cells is a reasonable header; require at least two labelled columns. */
const MIN_HEADER_CELLS = 2;
/** Only scan the first few rows for a header — real exports put it at the top, sometimes after a title row. */
const HEADER_SCAN_ROWS = 8;

const extensionOf = (fileName: string): string =>
  (fileName.split('.').pop() || '').toLowerCase();

const isBlank = (cell: any): boolean =>
  cell === null || cell === undefined || (typeof cell === 'string' && cell.trim() === '');

const cellToString = (cell: any): string => (isBlank(cell) ? '' : String(cell).trim());

/**
 * Pick the header row: the first row within the scan window that has at least `MIN_HEADER_CELLS`
 * non-blank cells and whose cells are mostly text (a data row of numbers is not a header). Returns the
 * row index, or -1 when no plausible header exists (caller then synthesizes `Column N` names).
 */
const findHeaderRow = (matrix: any[][]): number => {
  const limit = Math.min(HEADER_SCAN_ROWS, matrix.length);
  for (let i = 0; i < limit; i += 1) {
    const row = matrix[i] || [];
    const nonBlank = row.filter((c) => !isBlank(c));
    if (nonBlank.length < MIN_HEADER_CELLS) continue;
    const textCells = nonBlank.filter((c) => typeof c === 'string' || Number.isNaN(Number(c)));
    // A header row is predominantly labels, not values.
    if (textCells.length >= Math.ceil(nonBlank.length / 2)) return i;
  }
  return -1;
};

/** Make a de-duplicated, gap-filled list of column names from a header row (or generate them). */
const buildColumnNames = (headerRow: any[] | null, width: number): string[] => {
  const seen: Record<string, number> = {};
  const names: string[] = [];
  for (let c = 0; c < width; c += 1) {
    let base = headerRow ? cellToString(headerRow[c]) : '';
    if (!base) base = `Column ${c + 1}`;
    if (seen[base] === undefined) {
      seen[base] = 1;
      names.push(base);
    } else {
      seen[base] += 1;
      names.push(`${base}_${seen[base]}`);
    }
  }
  return names;
};

/**
 * Parse an uploaded spreadsheet/CSV into a `ParsedDataset`.
 *
 * @throws if the file extension is unsupported or the workbook has no readable sheet/rows.
 */
export async function parseDatasetFile(file: File): Promise<ParsedDataset> {
  const ext = extensionOf(file.name);
  const kind: DatasetSourceKind = CSV_EXTENSIONS.includes(ext) ? 'csv' : 'xlsx';
  if (!CSV_EXTENSIONS.includes(ext) && !EXCEL_EXTENSIONS.includes(ext)) {
    throw new Error(`Unsupported file type ".${ext}". Upload a .xlsx, .xls or .csv file.`);
  }

  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });

  // First sheet that actually has rows — some exports carry an empty cover/instructions sheet first.
  let sheetName: string | undefined;
  let matrix: any[][] = [];
  for (const name of workbook.SheetNames) {
    const sheet = workbook.Sheets[name];
    if (!sheet) continue;
    const rows = XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1, blankrows: false });
    if (rows.length > 0) {
      sheetName = name;
      matrix = rows as any[][];
      break;
    }
  }

  if (matrix.length === 0) {
    throw new Error('No rows found in the file. Check that the sheet is not empty.');
  }

  const width = matrix.reduce((max, row) => Math.max(max, row?.length || 0), 0);
  const headerIndex = findHeaderRow(matrix);
  const headerRow = headerIndex >= 0 ? matrix[headerIndex] : null;
  const columns = buildColumnNames(headerRow, width);

  const dataStart = headerIndex >= 0 ? headerIndex + 1 : 0;
  const rows: Record<string, any>[] = [];
  for (let r = dataStart; r < matrix.length; r += 1) {
    const raw = matrix[r] || [];
    if (raw.every((c) => isBlank(c))) continue;
    const obj: Record<string, any> = {};
    for (let c = 0; c < columns.length; c += 1) {
      obj[columns[c]] = raw[c] === undefined ? null : raw[c];
    }
    rows.push(obj);
  }

  return {
    columns,
    rows,
    rowCount: rows.length,
    matrix,
    sourceMeta: {
      fileName: file.name,
      parsedAt: new Date().toISOString(),
      kind,
      sheetName,
    },
  };
}
